// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../styles';
import {
  dispatchAttributeChangedEventTypesafe,
  event,
  ShuiLitElement,
} from '../../utils/event-decorator.js';
import { keyboardInteraction } from '../../utils/keyboardInteraction.js';
import { KEYCODE } from '../../utils/keycode.js';
import { TooltipEllipsisCheckerMixin } from '../../utils/tooltip-ellipsis-checker-mixin.js';
import userIdentifierStyles from './sh-user-identifier.lit.scss.js';
export const userIdentifierListOfProperties = [
  'image',
  'initials',
  'name',
  'info',
  'condensed',
  'unauthenticated',
];
/**
 * @fires image-changed {ImagePropertyChangedEvent} - *hide emitted when the image property changes.
 * @fires initials-changed {InitialsPropertyChangedEvent} - *hide emitted when the initials property changes.
 * @fires name-changed {NamePropertyChangedEvent} - *hide emitted when the name property changes.
 * @fires info-changed {InfoPropertyChangedEvent} - *hide emitted when the info property changes.
 * @fires condensed-changed {CondensedPropertyChangedEvent} - *hide emitted when the condensed property changes.
 * @fires unauthenticated-changed {UnauthenticatedPropertyChangedEvent} - *hide emitted when the unauthenticated property changes.
 */
let SHUserIdentifier = class SHUserIdentifier extends TooltipEllipsisCheckerMixin(ShuiLitElement) {
  static get styles() {
    return [userIdentifierStyles];
  }
  render() {
    return html`
      <div class="circle">
        ${this.unauthenticated
          ? html`
              ${this.name
                ? html` <sh-icon icon="user-avatar"></sh-icon> `
                : html` <sh-icon icon="user"></sh-icon> `}
            `
          : html`
              ${this.image
                ? html` <sh-icon icon="${this.image}"></sh-icon> `
                : html`
                    ${this.initials
                      ? this.formatInitials(this.initials)
                      : this.name
                        ? this.getInitials(this.name)
                        : nothing}
                  `}
            `}
      </div>
      ${this.name
        ? html`
            <div class="info-wrapper">
              <div class="name" @mouseenter="${(e) => this.showTooltip(e, this.name)}">
                ${this.name}
              </div>
              ${this.info && !this.unauthenticated
                ? html`
                    <div class="info" @mouseenter="${(e) => this.showTooltip(e, this.info)}">
                      ${this.info}
                    </div>
                  `
                : nothing}
            </div>
            <sh-tooltip placement="bottom-left" position-fixed-adjustment></sh-tooltip>
          `
        : nothing}
    `;
  }
  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
  }
  updated(changedProperties) {
    dispatchAttributeChangedEventTypesafe(this, changedProperties, userIdentifierListOfProperties);
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }
  firstUpdated() {
    this.addEventListener('focus', () => {
      this.onkeyup = (e) => {
        if (keyboardInteraction(e, KEYCODE.TAB)) {
          this.classList.add('focus');
        }
      };
    });
    this.addEventListener('blur', () => {
      this.classList.remove('focus');
    });
  }
  showTooltip(e, content) {
    const tooltip = this.renderRoot.querySelector('sh-tooltip');
    if (content && tooltip) {
      const target = e.target;
      const isEllipsis = this.ellipsisChecker(
        target.scrollWidth,
        target.offsetWidth,
        target.scrollHeight,
        target.offsetHeight,
        'horizontal'
      );
      if (isEllipsis) {
        tooltip.target = target;
        tooltip.visible = true;
        tooltip.label = content;
      } else {
        tooltip.target = '';
        tooltip.visible = false;
        tooltip.label = '';
      }
    }
  }
  getInitials(name) {
    const initials = name
      .split(/\s/)
      .reduce((response, word) => (response += word.slice(0, 1)), '');
    return this.formatInitials(initials);
  }
  formatInitials(initials) {
    const firstChar = initials.length > 0 ? initials.charAt(0) : '';
    const lastChar = initials.length > 1 ? initials.charAt(initials.length - 1) : '';
    return (firstChar + lastChar).toUpperCase();
  }
};
__decorate(
  [property({ type: String, reflect: true })],
  SHUserIdentifier.prototype,
  'image',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHUserIdentifier.prototype,
  'initials',
  void 0
);
__decorate([property({ type: String, reflect: true })], SHUserIdentifier.prototype, 'name', void 0);
__decorate([property({ type: String, reflect: true })], SHUserIdentifier.prototype, 'info', void 0);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHUserIdentifier.prototype,
  'condensed',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHUserIdentifier.prototype,
  'unauthenticated',
  void 0
);
__decorate([event()], SHUserIdentifier.prototype, 'imageChangedEvent', void 0);
__decorate([event()], SHUserIdentifier.prototype, 'initialsChangedEvent', void 0);
__decorate([event()], SHUserIdentifier.prototype, 'nameChangedEvent', void 0);
__decorate([event()], SHUserIdentifier.prototype, 'infoChangedEvent', void 0);
__decorate([event()], SHUserIdentifier.prototype, 'condensedChangedEvent', void 0);
__decorate([event()], SHUserIdentifier.prototype, 'unauthenticatedChangedEvent', void 0);
SHUserIdentifier = __decorate([customElement('sh-user-identifier')], SHUserIdentifier);
export { SHUserIdentifier };
